import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import {
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  ThermometerSnowflake,
  Route,
  FileCheck2,
  Activity,
  Crosshair,
  Star,
  Phone,
  Mail,
  MapPin,
  Clock,
  Truck,
  Building2,
  FlaskConical,
  Pill,
  Stethoscope,
  HeartPulse,
  ScanLine,
  Check,
  ChevronRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ---------- Reusable bits ---------- */
const Overline = ({ children, className = "" }) => (
  <span className={`overline text-[color:var(--mml-accent)] ${className}`}>{children}</span>
);

const SectionLabel = ({ index, label }) => (
  <div className="flex items-center gap-3 mb-6">
    <span className="font-mono text-xs text-[color:var(--mml-muted)]">{index}</span>
    <span className="h-px w-8 bg-[color:var(--mml-primary)]" />
    <Overline>{label}</Overline>
  </div>
);

/* ---------- Ticker Time (Newcastle) ---------- */
const useLiveTime = () => {
  const [t, setT] = useState(new Date());
  useEffect(() => {
    const i = setInterval(() => setT(new Date()), 1000);
    return () => clearInterval(i);
  }, []);
  return t;
};

/* ---------- Nav ---------- */
const Nav = () => {
  const time = useLiveTime();
  const hh = time.toLocaleTimeString("en-US", { hour12: false });

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-[color:var(--mml-bg)]/95 backdrop-blur border-b border-[color:var(--mml-border)]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-3 group"
        >
          <div className="w-9 h-9 bg-[color:var(--mml-primary)] flex items-center justify-center">
            <Crosshair className="w-5 h-5 text-[color:var(--mml-bg)]" strokeWidth={1.75} />
          </div>
          <div className="text-left leading-tight">
            <div className="font-display font-bold text-[15px] tracking-tight">MISSION MEDICAL</div>
            <div className="font-mono text-[10px] text-[color:var(--mml-muted)] tracking-widest">LOGISTICS · LLC</div>
          </div>
        </button>

        <nav className="hidden md:flex items-center gap-6 font-mono text-xs uppercase tracking-widest">
          {[
            ["regions", "01 / Regions"],
            ["services", "02 / Services"],
            ["who-we-serve", "03 / Sectors"],
            ["command", "04 / Command"],
            ["compliance", "05 / Compliance"],
            ["contact", "06 / Contact"],
          ].map(([id, label]) => (
            <button
              key={id}
              data-testid={`nav-${id}`}
              onClick={() => scrollTo(id)}
              className="text-[color:var(--mml-text)] hover:text-[color:var(--mml-accent)] transition-colors"
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <div className="font-mono text-[10px] text-[color:var(--mml-muted)] tracking-widest">
            NEWCASTLE · MD · TX · {hh}
          </div>
          <button
            data-testid="nav-cta-quote"
            onClick={() => scrollTo("contact")}
            className="bg-[color:var(--mml-accent)] text-white font-mono text-xs uppercase tracking-widest px-5 py-3 hover:bg-[color:var(--mml-accent-hover)] transition-colors"
          >
            Request Dispatch →
          </button>
        </div>

        <button
          data-testid="nav-cta-mobile"
          onClick={() => scrollTo("contact")}
          className="md:hidden bg-[color:var(--mml-accent)] text-white font-mono text-[11px] uppercase tracking-widest px-4 py-2"
        >
          Dispatch
        </button>
      </div>
    </header>
  );
};

/* ---------- Ribbon ---------- */
const Ribbon = () => {
  const items = [
    "SDVOSB / VOSB ELIGIBLE",
    "HIPAA COMPLIANT",
    "OSHA CERTIFIED HANDLERS",
    "DOT REGULATED FLEET",
    "CHAIN OF CUSTODY DOCUMENTED",
    "TEMPERATURE-CONTROLLED COLD CHAIN",
    "24 / 7 STAT DISPATCH",
    "GPS TRACKED",
  ];
  const doubled = [...items, ...items];
  return (
    <div
      data-testid="ribbon-marquee"
      className="bg-[color:var(--mml-primary)] text-[color:var(--mml-bg)] border-y border-black overflow-hidden py-3"
    >
      <div className="marquee-track font-mono text-xs uppercase tracking-[0.28em]">
        {doubled.map((it, i) => (
          <span key={i} className="mx-8 flex items-center gap-8 whitespace-nowrap">
            {it}
            <span className="text-[color:var(--mml-accent)]">✚</span>
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------- Hero ---------- */
const Hero = () => {
  const time = useLiveTime();
  const stamp = time.toISOString().split("T")[0] + " " + time.toLocaleTimeString("en-US", { hour12: false });

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" data-testid="section-hero" className="relative border-b border-[color:var(--mml-border)]">
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12">
        {/* Left */}
        <div className="lg:col-span-7 px-6 md:px-10 py-16 md:py-24 border-r border-[color:var(--mml-border)]">
          <SectionLabel index="00 / MML" label="Veteran-Owned Medical Courier" />

          <h1 className="font-display font-bold text-[44px] sm:text-6xl lg:text-[88px] leading-[0.95] tracking-tighter text-[color:var(--mml-primary)]">
            Every delivery<br />
            is a <span className="italic text-[color:var(--mml-accent)]">mission</span>.<br />
            Every mission<br />
            matters.
          </h1>

          <p className="mt-8 max-w-xl text-lg text-[color:var(--mml-text)] leading-relaxed">
            Mission Medical Logistics is a veteran-owned medical courier headquartered in Newcastle
            with regional operations across <span className="font-semibold">Maryland</span> and <span className="font-semibold">Texas</span> — delivering
            specimens, pharmaceuticals, records and equipment with military-grade discipline —
            <span className="font-mono text-[color:var(--mml-accent)]"> on time, every time.</span>
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("contact")}
              className="bg-[color:var(--mml-accent)] text-white font-mono text-xs uppercase tracking-widest px-8 py-4 hover:bg-[color:var(--mml-accent-hover)] transition-colors inline-flex items-center gap-2"
            >
              Request Dispatch <ArrowRight className="w-4 h-4" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("services")}
              className="border-2 border-[color:var(--mml-primary)] text-[color:var(--mml-primary)] font-mono text-xs uppercase tracking-widest px-8 py-4 hover:bg-[color:var(--mml-primary)] hover:text-[color:var(--mml-bg)] transition-colors inline-flex items-center gap-2"
            >
              View Services <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Stat strip */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 border-t border-l border-[color:var(--mml-border)]">
            {[
              ["24/7", "STAT Dispatch"],
              ["<60min", "Urban Response"],
              ["100%", "Chain of Custody"],
              ["2–8°C", "Cold Chain"],
            ].map(([n, l]) => (
              <div
                key={l}
                className="border-r border-b border-[color:var(--mml-border)] p-5"
                data-testid={`stat-${l.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                <div className="font-display text-3xl font-bold text-[color:var(--mml-primary)]">{n}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--mml-muted)] mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right image + meta */}
        <div className="lg:col-span-5 relative bg-[color:var(--mml-primary)] flex flex-col">
          <div className="relative flex-1 min-h-[380px] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Secure medical courier handoff"
              className="absolute inset-0 w-full h-full object-cover grayscale contrast-125"
            />
            <div className="absolute inset-0 bg-[color:var(--mml-primary)]/40 mix-blend-multiply" />
            <div className="absolute top-6 left-6 right-6 flex items-center justify-between text-[color:var(--mml-bg)] font-mono text-[10px] uppercase tracking-widest">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[color:var(--mml-accent)] blink" />
                LIVE · DISPATCH ONLINE
              </div>
              <div>UNIT · MML-01</div>
            </div>
            <div className="absolute bottom-6 left-6 right-6 border-t border-white/30 pt-4 flex flex-col gap-1 text-[color:var(--mml-bg)] font-mono text-[10px] uppercase tracking-widest">
              <div className="flex justify-between">
                <span>ORIGIN</span><span>BALTIMORE · MD · LAB-04</span>
              </div>
              <div className="flex justify-between">
                <span>DEST.</span><span>VA MED · WING C</span>
              </div>
              <div className="flex justify-between">
                <span>TEMP</span><span className="text-[color:var(--mml-accent)]">04.2 °C · NOMINAL</span>
              </div>
              <div className="flex justify-between">
                <span>ETA</span><span>00:14:22</span>
              </div>
            </div>
          </div>
          <div className="border-t border-black bg-[color:var(--mml-primary-hover)] text-[color:var(--mml-bg)] px-6 py-4 font-mono text-[10px] uppercase tracking-widest flex items-center justify-between">
            <span>{stamp} UTC</span>
            <span className="text-[color:var(--mml-accent)]">◉ MISSION ACTIVE</span>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Regional Coverage ---------- */
const regions = [
  {
    code: "R · 01",
    state: "Newcastle",
    role: "Headquarters · Command",
    note: "Dispatch center, fleet operations, primary training facility, and 24/7 STAT hub.",
  },
  {
    code: "R · 02",
    state: "Maryland",
    role: "Regional Operations",
    note: "Coverage across Baltimore, Bethesda, and the DC-metro corridor — labs, VA facilities, and NIH-adjacent clinics.",
  },
  {
    code: "R · 03",
    state: "Texas",
    role: "Regional Operations",
    note: "Coverage across Houston, Dallas–Fort Worth, and San Antonio — hospital systems, specialty pharmacies, and reference labs.",
  },
];

const RegionalCoverage = () => (
  <section
    id="regions"
    data-testid="section-regions"
    className="border-b border-[color:var(--mml-border)] bg-[color:var(--mml-surface)]"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div>
          <SectionLabel index="0A" label="Areas of Operation" />
          <h2 className="font-display font-bold text-4xl md:text-6xl tracking-tighter text-[color:var(--mml-primary)] max-w-3xl leading-[0.95]">
            Three states.<br />
            One standard.
          </h2>
        </div>
        <p className="max-w-md text-[color:var(--mml-muted)] leading-relaxed">
          Regional operations spanning the Mid-Atlantic and Gulf corridors, dispatched and monitored
          from a single command center — the same discipline, no matter which zip code you&apos;re in.
        </p>
      </div>
    </div>

    <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-3 border-t border-l border-[color:var(--mml-border)]">
      {regions.map((r, i) => (
        <div
          key={r.state}
          data-testid={`region-${i + 1}`}
          className="border-r border-b border-[color:var(--mml-border)] p-8 flex flex-col gap-4 bg-[color:var(--mml-bg)]"
        >
          <div className="flex items-center justify-between">
            <MapPin
              className="w-7 h-7 text-[color:var(--mml-accent)]"
              strokeWidth={1.5}
            />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--mml-muted)]">
              {r.code}
            </span>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--mml-accent)] mb-2">
              {r.role}
            </div>
            <h3 className="font-display text-3xl font-bold tracking-tight text-[color:var(--mml-primary)]">
              {r.state}
            </h3>
            <p className="text-sm text-[color:var(--mml-muted)] mt-3 leading-relaxed">
              {r.note}
            </p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

/* ---------- Services ---------- */
const services = [
  {
    icon: Activity,
    code: "S · 01",
    title: "Same-Day & STAT",
    body: "Rapid pickup for urgent lab specimens, pharmaceuticals and time-critical documents. Guaranteed windows for STAT and routine calls.",
  },
  {
    icon: ThermometerSnowflake,
    code: "S · 02",
    title: "Cold Chain Transport",
    body: "2–8 °C insulated compartments with continuous temperature monitoring for biologics, vaccines and specialty pharmaceuticals.",
  },
  {
    icon: Route,
    code: "S · 03",
    title: "Real-Time Tracking",
    body: "GPS-enabled fleet with electronic proof of delivery, signature/photo capture, and full digital chain of custody.",
  },
  {
    icon: Clock,
    code: "S · 04",
    title: "Scheduled Routes",
    body: "Recurring routes, on-call service, after-hours and weekend windows — engineered around your operational schedule.",
  },
  {
    icon: FileCheck2,
    code: "S · 05",
    title: "Chain of Custody",
    body: "Auditable, time-stamped custody logs for every specimen — designed for HIPAA reviews and compliance inspections.",
  },
  {
    icon: ShieldCheck,
    code: "S · 06",
    title: "Compliance Handling",
    body: "HIPAA, OSHA bloodborne pathogens, and DOT-compliant procedures with documented driver training and SOPs.",
  },
  {
    icon: Truck,
    code: "S · 07",
    title: "Equipment & Supplies",
    body: "Secure inter-facility transport of medical devices, records and supplies for hospital systems and clinics.",
  },
  {
    icon: ScanLine,
    code: "S · 08",
    title: "Digital Reporting",
    body: "Client portal with delivery history, temperature logs, exception reports and monthly performance dashboards.",
  },
];

const Services = () => (
  <section id="services" data-testid="section-services" className="border-b border-[color:var(--mml-border)]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div>
          <SectionLabel index="01" label="Field Operations" />
          <h2 className="font-display font-bold text-4xl md:text-6xl tracking-tighter text-[color:var(--mml-primary)] max-w-3xl leading-[0.95]">
            Eight lines of service.<br />Zero tolerance for late.
          </h2>
        </div>
        <p className="max-w-md text-[color:var(--mml-muted)] leading-relaxed">
          From STAT specimen transport to recurring cold-chain routes, every mission runs on the same
          non-negotiable standard: verified custody, monitored temperature, documented delivery.
        </p>
      </div>
    </div>

    <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-[color:var(--mml-border)]">
      {services.map((s, i) => {
        const Icon = s.icon;
        return (
          <div
            key={s.code}
            data-testid={`service-card-${i + 1}`}
            className="relative border-r border-b border-[color:var(--mml-border)] p-8 bg-[color:var(--mml-surface)] hover:bg-[color:var(--mml-primary)] hover:text-[color:var(--mml-bg)] transition-colors group cursor-default"
          >
            <div className="flex items-center justify-between mb-8">
              <Icon className="w-8 h-8 text-[color:var(--mml-primary)] group-hover:text-[color:var(--mml-accent)] transition-colors" strokeWidth={1.5} />
              <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--mml-muted)] group-hover:text-[color:var(--mml-bg)]/70">{s.code}</span>
            </div>
            <h3 className="font-display text-xl font-semibold tracking-tight mb-3">{s.title}</h3>
            <p className="text-sm leading-relaxed text-[color:var(--mml-muted)] group-hover:text-[color:var(--mml-bg)]/85">{s.body}</p>
            <div className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[color:var(--mml-accent)] opacity-0 group-hover:opacity-100 transition-opacity">
              Operational <ArrowUpRight className="w-3 h-3" />
            </div>
          </div>
        );
      })}
    </div>
  </section>
);

/* ---------- Who We Serve ---------- */
const sectors = [
  { icon: Building2, label: "Hospitals & Health Systems", note: "Inter-facility specimen and supply transport." },
  { icon: FlaskConical, label: "Reference Laboratories", note: "Scheduled and STAT specimen pickup." },
  { icon: Pill, label: "Retail & Specialty Pharmacies", note: "Temperature-controlled medication delivery." },
  { icon: Stethoscope, label: "Physician & Urgent Care Clinics", note: "On-demand courier for outpatient volume." },
  { icon: HeartPulse, label: "Long-Term Care & Hospice", note: "Reliable pharmacy and records routing." },
  { icon: ShieldCheck, label: "VA & Government Facilities", note: "SDVOSB-eligible set-aside contracts." },
];

const WhoWeServe = () => (
  <section id="who-we-serve" data-testid="section-sectors" className="border-b border-[color:var(--mml-border)]">
    <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12">
      <div className="lg:col-span-5 px-6 md:px-10 py-16 md:py-24 border-r border-[color:var(--mml-border)] bg-[color:var(--mml-surface)]">
        <SectionLabel index="02" label="Sectors Under Contract" />
        <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tighter text-[color:var(--mml-primary)] leading-[0.95] mb-6">
          Built for the healthcare supply chain.
        </h2>
        <p className="text-[color:var(--mml-muted)] leading-relaxed mb-8">
          MML serves the full spectrum of healthcare operators across our three-state footprint —
          <span className="text-[color:var(--mml-primary)] font-semibold"> Newcastle, Maryland, and Texas</span> —
          the institutions where a missed pickup can mean a missed diagnosis, a compromised biologic,
          or a broken compliance record.
        </p>
        <div className="border border-[color:var(--mml-border)] p-6 bg-[color:var(--mml-bg)]">
          <Overline>Veteran Business Priority</Overline>
          <p className="mt-3 font-display text-lg text-[color:var(--mml-primary)]">
            VOSB & SDVOSB eligible — direct pathway to VA, federal and supplier-diversity contracts.
          </p>
        </div>
      </div>

      <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 border-l border-t lg:border-t-0 border-[color:var(--mml-border)]">
        {sectors.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={s.label}
              data-testid={`sector-${i + 1}`}
              className="p-8 border-r border-b border-[color:var(--mml-border)] flex flex-col gap-4 bg-[color:var(--mml-bg)] hover:bg-[color:var(--mml-surface)] transition-colors"
            >
              <div className="flex items-center justify-between">
                <Icon className="w-7 h-7 text-[color:var(--mml-primary)]" strokeWidth={1.5} />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--mml-muted)]">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold text-[color:var(--mml-primary)]">{s.label}</h3>
                <p className="text-sm text-[color:var(--mml-muted)] mt-1">{s.note}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------- Command (About / Veteran Story) ---------- */
const Command = () => (
  <section id="command" data-testid="section-command" className="bg-[color:var(--mml-primary)] text-[color:var(--mml-bg)] border-b border-black">
    <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12">
      <div className="lg:col-span-7 px-6 md:px-10 py-16 md:py-24 border-r border-black/40">
        <div className="flex items-center gap-3 mb-6">
          <span className="font-mono text-xs text-[color:var(--mml-bg)]/60">03</span>
          <span className="h-px w-8 bg-[color:var(--mml-accent)]" />
          <span className="overline text-[color:var(--mml-accent)]">Command Profile</span>
        </div>

        <h2 className="font-display font-bold text-4xl md:text-6xl tracking-tighter leading-[0.95]">
          Led by a veteran.<br />
          Run like a mission.
        </h2>

        <p className="mt-8 max-w-2xl text-[color:var(--mml-bg)]/85 text-lg leading-relaxed">
          Mission Medical Logistics is founded and led by <span className="text-white font-semibold">Deborah Thompson</span>,
          Chief Executive Officer and President. The company was built on a single premise:
          the discipline, accountability and precision instilled through military service belong in
          the healthcare supply chain — where a late delivery is never just late.
        </p>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-0 border-t border-l border-white/20">
          {[
            ["Integrity", "Every delivery handled with honesty and full accountability."],
            ["Discipline", "Punctuality and precision, instilled through military service."],
            ["Compliance", "Unwavering commitment to HIPAA, OSHA and DOT standards."],
            ["Service", "Every client relationship treated as a long-term partnership."],
          ].map(([v, d], i) => (
            <div
              key={v}
              data-testid={`value-${i + 1}`}
              className="border-r border-b border-white/20 p-6"
            >
              <div className="flex items-center gap-3 mb-2">
                <Check className="w-4 h-4 text-[color:var(--mml-accent)]" strokeWidth={2.5} />
                <div className="font-display text-xl font-semibold">{v}</div>
              </div>
              <p className="text-sm text-[color:var(--mml-bg)]/70 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>

        <blockquote className="mt-14 border-l-4 border-[color:var(--mml-accent)] pl-6 max-w-2xl">
          <p className="font-display text-2xl italic text-white leading-snug">
            &ldquo;Mission Medical Logistics exists to deliver what matters most — on time, every time —
            with the same discipline and accountability instilled through military service.&rdquo;
          </p>
          <footer className="mt-4 font-mono text-xs uppercase tracking-widest text-[color:var(--mml-bg)]/60">
            Deborah Thompson · CEO & President
          </footer>
        </blockquote>
      </div>

      <div className="lg:col-span-5 relative min-h-[400px]">
        <img
          src="https://images.unsplash.com/photo-1757314428300-b34bb991b860?fm=jpg&q=85&w=2000&auto=format&fit=crop"
          alt="F-35 Lightning II — U.S. Air Force"
          className="absolute inset-0 w-full h-full object-cover grayscale contrast-110"
        />
        <div className="absolute inset-0 bg-[color:var(--mml-primary)]/55 mix-blend-multiply" />
        <div className="absolute top-6 left-6 right-6 flex items-center justify-between text-[color:var(--mml-bg)] font-mono text-[10px] uppercase tracking-widest">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-[color:var(--mml-accent)]" />
            SERVICE RECORD · USAF
          </div>
          <div>F-35A · LIGHTNING II</div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-8 border-t border-white/20 backdrop-blur-sm bg-black/40">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--mml-accent)] mb-2">
            OFFICER · IN COMMAND
          </div>
          <div className="font-display text-2xl font-bold text-white">Deborah Thompson</div>
          <div className="font-mono text-xs text-white/70 mt-1">CEO &amp; PRESIDENT · MML LLC</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mt-3 border-t border-white/15 pt-3">
            U.S. AIR FORCE VETERAN · F-35 PROGRAM
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Compliance ---------- */
const Compliance = () => (
  <section id="compliance" data-testid="section-compliance" className="border-b border-[color:var(--mml-border)]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionLabel index="04" label="Regulatory Framework" />
          <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tighter text-[color:var(--mml-primary)] leading-[0.95]">
            Auditable by design.
          </h2>
          <p className="mt-6 text-[color:var(--mml-muted)] leading-relaxed">
            Every route, every handoff, every temperature reading is logged, time-stamped and retrievable —
            purpose-built for the compliance officers, procurement teams and inspectors who evaluate our clients.
          </p>
        </div>

        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 border-t border-l border-[color:var(--mml-border)]">
          {[
            { code: "HIPAA", title: "Privacy & PHI Handling", note: "Staff trained on patient privacy, secure record transport, and breach protocols." },
            { code: "OSHA", title: "Bloodborne Pathogens", note: "Documented handling of biological specimens with certified courier training." },
            { code: "DOT", title: "Transportation Compliance", note: "Driver qualification files, vehicle inspection logs, and safety-hours discipline." },
            { code: "SDVOSB", title: "Veteran-Owned Certification", note: "Eligible for VA, federal, and diversity set-aside contracting programs." },
          ].map((c, i) => (
            <div
              key={c.code}
              data-testid={`compliance-${c.code.toLowerCase()}`}
              className="p-8 border-r border-b border-[color:var(--mml-border)] bg-[color:var(--mml-surface)]"
            >
              <div className="font-mono text-xs tracking-widest text-[color:var(--mml-accent)] mb-3">{c.code} · CERTIFIED READY</div>
              <div className="font-display text-lg font-semibold text-[color:var(--mml-primary)] mb-2">{c.title}</div>
              <p className="text-sm text-[color:var(--mml-muted)] leading-relaxed">{c.note}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Testimonials ---------- */
const testimonials = [
  {
    quote: "MML picks up STAT specimens 40 minutes faster than our previous courier. Their custody logs pass every audit without a question.",
    who: "Lab Operations Director",
    org: "Regional Reference Laboratory",
  },
  {
    quote: "Cold chain integrity has been flawless. Their temperature logs are the cleanest documentation we've received from any vendor.",
    who: "Pharmacy Manager",
    org: "Specialty Infusion Pharmacy",
  },
  {
    quote: "As a VA-adjacent facility, working with an SDVOSB-eligible partner isn't a bonus — it's essential. MML delivers on both counts.",
    who: "Procurement Lead",
    org: "Veteran Health Network",
  },
];

const Testimonials = () => (
  <section data-testid="section-testimonials" className="border-b border-[color:var(--mml-border)] bg-[color:var(--mml-surface)]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
      <SectionLabel index="05" label="Field Reports" />
      <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tighter text-[color:var(--mml-primary)] mb-12 max-w-3xl leading-[0.95]">
        What the field says about the mission.
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-[color:var(--mml-border)]">
        {testimonials.map((t, i) => (
          <div
            key={i}
            data-testid={`testimonial-${i + 1}`}
            className="p-8 border-r border-b border-[color:var(--mml-border)] flex flex-col gap-6"
          >
            <div className="flex gap-1">
              {[...Array(5)].map((_, j) => (
                <Star key={j} className="w-4 h-4 fill-[color:var(--mml-accent)] text-[color:var(--mml-accent)]" strokeWidth={0} />
              ))}
            </div>
            <p className="font-display text-lg text-[color:var(--mml-primary)] leading-snug">
              &ldquo;{t.quote}&rdquo;
            </p>
            <div className="mt-auto pt-4 border-t border-[color:var(--mml-border)]">
              <div className="font-mono text-xs uppercase tracking-widest text-[color:var(--mml-primary)]">{t.who}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--mml-muted)] mt-1">{t.org}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Contact ---------- */
const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    role: "",
    phone: "",
    service_interest: "Same-Day / STAT",
    volume_estimate: "",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/leads`, form);
      setStatus({ state: "success", msg: "Mission acknowledged. A dispatch officer will respond within 1 business day." });
      setForm({
        name: "",
        email: "",
        organization: "",
        role: "",
        phone: "",
        service_interest: "Same-Day / STAT",
        volume_estimate: "",
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        state: "error",
        msg: typeof detail === "string" ? detail : "Transmission failed. Please verify fields and retry.",
      });
    }
  };

  const inputBase =
    "w-full bg-[color:var(--mml-bg)] border border-[color:var(--mml-border)] px-4 py-3 rounded-none focus:border-[color:var(--mml-accent)] focus:ring-0 outline-none placeholder:text-[color:var(--mml-muted)] font-mono text-sm text-[color:var(--mml-text)]";

  return (
    <section id="contact" data-testid="section-contact" className="border-b border-[color:var(--mml-border)]">
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12">
        <div className="lg:col-span-5 px-6 md:px-10 py-16 md:py-24 bg-[color:var(--mml-primary)] text-[color:var(--mml-bg)] border-r border-black">
          <div className="flex items-center gap-3 mb-6">
            <span className="font-mono text-xs text-white/60">06</span>
            <span className="h-px w-8 bg-[color:var(--mml-accent)]" />
            <span className="overline text-[color:var(--mml-accent)]">Open A Dispatch Line</span>
          </div>

          <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tighter leading-[0.95]">
            Request a route.<br />
            Start a mission.
          </h2>

          <p className="mt-6 text-white/80 leading-relaxed">
            Send us a brief. Our dispatch team will respond within one business day with a tailored proposal,
            reference documentation, and a trial delivery window if requested.
          </p>

          <div className="mt-12 space-y-6">
            {[
              [Phone, "Dispatch Line", "+1 (555) MML-STAT"],
              [Mail, "Email", "dispatch@missionmedlogistics.com"],
              [MapPin, "Regional Coverage", "Newcastle · Maryland · Texas"],
              [Clock, "Operations", "24 / 7 · STAT Standby"],
            ].map(([Icon, label, val]) => (
              <div key={label} className="flex items-start gap-4 border-t border-white/15 pt-4">
                <Icon className="w-5 h-5 text-[color:var(--mml-accent)] mt-0.5" strokeWidth={1.5} />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/60">{label}</div>
                  <div className="font-display text-lg text-white">{val}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7 px-6 md:px-10 py-16 md:py-24 bg-[color:var(--mml-surface)]">
          <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="overline block mb-2">Full Name</label>
                <input
                  data-testid="input-name"
                  required
                  value={form.name}
                  onChange={onChange("name")}
                  className={inputBase}
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label className="overline block mb-2">Work Email</label>
                <input
                  data-testid="input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={onChange("email")}
                  className={inputBase}
                  placeholder="jane@hospital.org"
                />
              </div>
              <div>
                <label className="overline block mb-2">Organization</label>
                <input
                  data-testid="input-organization"
                  required
                  value={form.organization}
                  onChange={onChange("organization")}
                  className={inputBase}
                  placeholder="Newcastle / MD / TX Regional Lab"
                />
              </div>
              <div>
                <label className="overline block mb-2">Role</label>
                <input
                  data-testid="input-role"
                  value={form.role}
                  onChange={onChange("role")}
                  className={inputBase}
                  placeholder="Lab Operations Director"
                />
              </div>
              <div>
                <label className="overline block mb-2">Phone</label>
                <input
                  data-testid="input-phone"
                  value={form.phone}
                  onChange={onChange("phone")}
                  className={inputBase}
                  placeholder="+1 555 000 0000"
                />
              </div>
              <div>
                <label className="overline block mb-2">Service Interest</label>
                <select
                  data-testid="input-service"
                  value={form.service_interest}
                  onChange={onChange("service_interest")}
                  className={inputBase}
                >
                  <option>Same-Day / STAT</option>
                  <option>Scheduled Routes</option>
                  <option>Cold Chain Transport</option>
                  <option>Chain of Custody / Records</option>
                  <option>Equipment & Supplies</option>
                  <option>VA / Government Contract</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Estimated Weekly Volume (optional)</label>
                <input
                  data-testid="input-volume"
                  value={form.volume_estimate}
                  onChange={onChange("volume_estimate")}
                  className={inputBase}
                  placeholder="e.g. 20 STAT pickups / week"
                />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Mission Brief</label>
                <textarea
                  data-testid="input-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={onChange("message")}
                  className={`${inputBase} resize-none`}
                  placeholder="Tell us about your routes, current courier pain points, or compliance requirements."
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-[color:var(--mml-border)]">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--mml-muted)] max-w-md">
                By submitting, you agree to be contacted by MML dispatch. We do not share your information.
              </p>
              <button
                data-testid="submit-lead"
                type="submit"
                disabled={status.state === "loading"}
                className="bg-[color:var(--mml-accent)] text-white font-mono text-xs uppercase tracking-widest px-8 py-4 hover:bg-[color:var(--mml-accent-hover)] transition-colors inline-flex items-center gap-2 disabled:opacity-60"
              >
                {status.state === "loading" ? "Transmitting…" : "Transmit Brief"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {status.state === "success" && (
              <div
                data-testid="form-success"
                className="border border-[color:var(--mml-primary)] bg-[color:var(--mml-primary)] text-[color:var(--mml-bg)] p-4 font-mono text-xs uppercase tracking-widest"
              >
                ✚ {status.msg}
              </div>
            )}
            {status.state === "error" && (
              <div
                data-testid="form-error"
                className="border border-[color:var(--mml-accent)] bg-[color:var(--mml-accent)]/10 text-[color:var(--mml-accent)] p-4 font-mono text-xs uppercase tracking-widest"
              >
                ✕ {status.msg}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

/* ---------- Footer ---------- */
const Footer = () => (
  <footer data-testid="site-footer" className="bg-[color:var(--mml-primary)] text-[color:var(--mml-bg)]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 border-b border-white/10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-6">
          <div className="overline text-[color:var(--mml-accent)]">Standing Order</div>
          <h3 className="font-display font-bold text-4xl md:text-6xl tracking-tighter leading-[0.95] mt-4">
            Every delivery is<br />
            a mission.<br />
            <span className="text-[color:var(--mml-accent)]">Every mission matters.</span>
          </h3>
        </div>

        <div className="lg:col-span-6 grid grid-cols-2 gap-8">
          <div>
            <div className="overline text-white/60 mb-4">Operations</div>
            <ul className="space-y-2 font-mono text-sm">
              <li>HQ · Newcastle</li>
              <li>Regional · Maryland</li>
              <li>Regional · Texas</li>
              <li>24 / 7 STAT Standby</li>
            </ul>
          </div>
          <div>
            <div className="overline text-white/60 mb-4">Certifications</div>
            <ul className="space-y-2 font-mono text-sm">
              <li>SDVOSB / VOSB Eligible</li>
              <li>HIPAA Compliant</li>
              <li>OSHA Trained</li>
              <li>DOT Regulated</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 font-mono text-[10px] uppercase tracking-widest text-white/60">
      <div>© {new Date().getFullYear()} Mission Medical Logistics LLC · Veteran-Owned · Newcastle · Maryland · Texas</div>
      <div>Built with discipline · Delivered with integrity</div>
    </div>
  </footer>
);

/* ---------- App ---------- */
function App() {
  useEffect(() => {
    axios.get(`${API}/`).catch(() => {});
  }, []);

  return (
    <div className="App font-body">
      <Nav />
      <Hero />
      <Ribbon />
      <RegionalCoverage />
      <Services />
      <WhoWeServe />
      <Command />
      <Compliance />
      <Testimonials />
      <Contact />
      <Footer />
    </div>
  );
}

export default App;
